import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import PlayerAvatar from "../components/PlayerAvatar";
import Stars from "../components/Stars";
import DocumentsTab from "../components/DocumentsTab";
import InviteParentModal from "../components/InviteParentModal";
import {
  ArrowLeft, Phone, ChatCircle, Envelope, FilePdf, PencilSimple,
  Star, TrashSimple, PaperPlaneRight, Plus, Trash, PaperPlaneTilt, X, Sparkle, UserPlus
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SKILL_CATEGORIES = {
  technical: { label: "Technical", keys: ["passing", "receiving", "first_touch", "dribbling", "shooting", "finishing", "ball_control", "crossing", "heading"] },
  physical: { label: "Physical", keys: ["speed", "strength", "agility", "stamina", "balance"] },
  defending: { label: "Defending", keys: ["tackling", "positioning", "marking"] },
  mental: { label: "Mental", keys: ["vision", "decision_making", "confidence", "leadership", "communication"] },
  behavior: { label: "Behavior", keys: ["discipline", "respect", "coachability", "teamwork", "sportsmanship", "attitude"] },
};

const TABS = ["Overview", "Personal", "Parent", "Medical", "Skills", "Attendance", "Matches", "Notes", "Documents", "Progress"];

const labelize = (s) => s.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [skills, setSkills] = useState({});
  const [skillHistory, setSkillHistory] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [matchStats, setMatchStats] = useState([]);
  const [matches, setMatches] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingSkills, setSavingSkills] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  async function sendReport() {
    if (!emailTo) return;
    setEmailing(true);
    try {
      await api.post(`/emails/report/${id}`, { to_email: emailTo, message: emailMsg });
      toast.success(`Report sent to ${emailTo}`);
      setEmailOpen(false);
      setEmailMsg("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailing(false);
    }
  }

  async function generateSummary() {
    setSummarizing(true);
    try {
      const r = await api.post(`/players/${id}/summary`, {});
      toast.success("AI summary generated");
      setPlayer((prev) => prev ? { ...prev, ai_summary: r.data.summary, ai_summary_at: r.data.generated_at } : prev);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSummarizing(false); }
  }

  async function reload() {
    const [p, skHist, att, ns, ms] = await Promise.all([
      api.get(`/players/${id}`),
      api.get(`/skills/${id}`),
      api.get(`/attendance?player_id=${id}`),
      api.get(`/notes/${id}`),
      api.get(`/matches`),
    ]);
    setPlayer(p.data);
    setSkillHistory(skHist.data);
    setSkills(skHist.data[0]?.ratings || {});
    setAttendance(att.data);
    setNotes(ns.data);
    setMatches(ms.data);

    // Fetch all match stats for this player across matches (efficient enough for MVP)
    const statsPromises = ms.data.map((m) => api.get(`/match-stats/${m.id}`));
    const results = await Promise.all(statsPromises);
    const mine = [];
    results.forEach((r, i) => {
      const found = r.data.find((s) => s.player_id === id);
      if (found) mine.push({ ...found, match: ms.data[i] });
    });
    setMatchStats(mine);
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  async function saveSkills() {
    setSavingSkills(true);
    try {
      await api.post("/skills", { player_id: id, ratings: skills });
      toast.success("Skills saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSkills(false); }
  }

  async function addNote() {
    if (!noteText.trim()) return;
    try {
      await api.post("/notes", { player_id: id, text: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteNote(nid) {
    await api.delete(`/notes/${nid}`);
    reload();
  }

  async function deletePlayer() {
    if (!window.confirm("Delete this player? This removes all their records.")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Player deleted");
      navigate("/players");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const totals = useMemo(() => {
    return matchStats.reduce((acc, s) => ({
      goals: acc.goals + (s.goals || 0),
      assists: acc.assists + (s.assists || 0),
      minutes: acc.minutes + (s.minutes || 0),
      tackles: acc.tackles + (s.tackles || 0),
    }), { goals: 0, assists: 0, minutes: 0, tackles: 0 });
  }, [matchStats]);

  const progressData = useMemo(() => {
    return skillHistory.slice().reverse().map((h) => {
      const vals = Object.values(h.ratings || {}).filter((v) => v);
      return {
        date: h.date,
        overall: vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : 0,
      };
    });
  }, [skillHistory]);

  function downloadReport() {
    const token = localStorage.getItem("wahdat_token");
    const url = `${API_BASE}/reports/player/${id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        window.open(u, "_blank");
      })
      .catch(() => toast.error("Could not generate report"));
  }

  if (!player) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft size={16} weight="bold" /> Back
      </button>

      {/* Header */}
      <div className="bg-[#121212] border border-neutral-800 rounded-lg overflow-hidden">
        <div
          className="h-40 md:h-56 relative"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.85) 100%), linear-gradient(135deg, rgba(217,4,41,0.45), rgba(6,214,160,0.15)), url('https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxzb2NjZXIlMjBmaWVsZCUyMG5pZ2h0fGVufDB8fHx8MTc4NDg1NjkwN3ww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="p-6 flex flex-col md:flex-row md:items-end gap-6 -mt-16">
          <PlayerAvatar player={player} size={128} className="border-4 border-[#121212] shadow-xl" />
          <div className="flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">
                {player.first_name} {player.last_name}
              </h1>
              {player.jersey_number ? (
                <div className="text-[#D90429] font-display font-black text-3xl">#{player.jersey_number}</div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-neutral-400">
              <span className="overline">{player.position || "—"}</span>
              {player.age && <span className="overline">Age {player.age}</span>}
              {player.team && <span className="overline">{player.team}</span>}
            </div>
            <div className="flex flex-wrap gap-6 mt-4">
              <div>
                <div className="overline">Attendance</div>
                <div className="font-display font-bold text-2xl text-[#06D6A0]">{player.attendance_pct}%</div>
              </div>
              <div>
                <div className="overline">Overall</div>
                <div className="font-display font-bold text-2xl text-[#FFD166] flex items-center gap-1">
                  <Star size={20} weight="fill" /> {player.overall_rating || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-neutral-800 grid grid-cols-3 sm:grid-cols-7">
          {[
            { icon: Phone, label: "Call", href: player.phone ? `tel:${player.phone}` : null, testid: "call-parent" },
            { icon: ChatCircle, label: "Text", href: player.phone ? `sms:${player.phone}` : null, testid: "text-parent" },
            { icon: Envelope, label: "Email", href: player.email ? `mailto:${player.email}` : null, testid: "email-parent" },
            { icon: FilePdf, label: "Report", onClick: downloadReport, testid: "generate-report" },
            { icon: PaperPlaneTilt, label: "Send", onClick: () => { setEmailTo(player?.email || ""); setEmailOpen(true); }, testid: "email-report-btn" },
            { icon: UserPlus, label: "Invite", onClick: () => setInviteOpen(true), testid: "invite-parent-btn" },
            { icon: PencilSimple, label: "Edit", to: `/players/${id}/edit`, testid: "edit-player" },
          ].map((b) => {
            const Base = ({ children }) =>
              b.to ? <Link to={b.to} className="w-full">{children}</Link> :
              b.onClick ? <button onClick={b.onClick} className="w-full">{children}</button> :
              <a href={b.href || "#"} onClick={(e) => !b.href && e.preventDefault()} className="w-full">{children}</a>;
            return (
              <Base key={b.label}>
                <div
                  className={`h-16 flex items-center justify-center gap-2 border-r border-neutral-800 last:border-r-0 text-sm font-bold uppercase tracking-wider ${b.href || b.to || b.onClick ? "hover:bg-neutral-900 text-white" : "text-neutral-600"}`}
                  data-testid={b.testid}
                >
                  <b.icon size={20} weight="bold" /> {b.label}
                </div>
              </Base>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-neutral-800 -mx-4 px-4 sm:-mx-6 sm:px-6 gap-1 no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === t ? "border-[#D90429] text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Goals" value={totals.goals} color="#D90429" />
            <Stat label="Assists" value={totals.assists} color="#FFD166" />
            <Stat label="Minutes" value={totals.minutes} color="#06D6A0" />
            <Stat label="Tackles" value={totals.tackles} color="#F5F5F5" />
          </div>
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 mt-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 overline">
                  <Sparkle size={14} weight="fill" color="#FFD166" /> Coach's AI Summary
                </div>
                {player.ai_summary ? (
                  <>
                    <p className="text-sm text-neutral-200 mt-3 leading-relaxed">{player.ai_summary}</p>
                    {player.ai_summary_at && (
                      <div className="overline mt-3">Generated {new Date(player.ai_summary_at).toLocaleDateString()}</div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-neutral-500 mt-3">No AI summary yet. Click generate to create one for the next parent report.</p>
                )}
              </div>
              <button
                onClick={generateSummary}
                disabled={summarizing}
                data-testid="ai-summary-btn"
                className="h-11 px-4 rounded-md bg-[#FFD166] hover:bg-[#e8bd5d] text-[#050505] font-display font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
              >
                <Sparkle size={14} weight="fill" /> {summarizing ? "Thinking…" : (player.ai_summary ? "Regenerate" : "Generate")}
              </button>
            </div>
          </div>
        </>
      )}

      {tab === "Personal" && (
        <InfoGrid rows={[
          ["Nickname", player.nickname], ["Preferred Foot", player.preferred_foot],
          ["Date of Birth", player.date_of_birth], ["Age", player.age],
          ["Height", player.height_cm ? `${player.height_cm} cm` : "—"],
          ["Weight", player.weight_kg ? `${player.weight_kg} kg` : "—"],
          ["Secondary Position", player.secondary_position], ["Team", player.team],
          ["Age Group", player.age_group], ["Date Joined", player.date_joined],
        ]} />
      )}

      {tab === "Parent" && (
        <InfoGrid rows={[
          ["Father", player.father_name], ["Mother", player.mother_name],
          ["Guardian", player.guardian], ["Phone", player.phone],
          ["Secondary Phone", player.phone_secondary], ["Email", player.email],
          ["Address", player.address], ["Emergency Contact", player.emergency_contact],
        ]} />
      )}

      {tab === "Medical" && (
        <InfoGrid rows={[
          ["Allergies", player.allergies], ["Asthma", player.asthma ? "Yes" : "No"],
          ["Injuries", player.injuries], ["Medications", player.medications],
          ["Doctor Notes", player.doctor_notes],
        ]} />
      )}

      {tab === "Skills" && (
        <div className="space-y-6">
          {Object.entries(SKILL_CATEGORIES).map(([cat, def]) => (
            <div key={cat} className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
              <div className="overline mb-4">{def.label}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {def.keys.map((k) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold">{labelize(k)}</div>
                    <Stars
                      testid={`skill-${k}`}
                      value={skills[k] || 0}
                      onChange={(v) => setSkills((s) => ({ ...s, [k]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <button
              onClick={saveSkills}
              disabled={savingSkills}
              data-testid="save-skills-btn"
              className="h-14 px-8 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider disabled:opacity-60"
            >
              {savingSkills ? "Saving…" : "Save Ratings"}
            </button>
          </div>
        </div>
      )}

      {tab === "Attendance" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg divide-y divide-neutral-900">
          {attendance.length === 0 ? (
            <div className="p-6 text-neutral-500 text-sm">No attendance records yet.</div>
          ) : attendance.map((a) => (
            <div key={a.id} className="p-4 flex items-center justify-between">
              <div className="text-sm">
                <div className="font-semibold">{a.date}</div>
                <div className="overline">{a.session_type}</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      )}

      {tab === "Matches" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg divide-y divide-neutral-900">
          {matchStats.length === 0 ? (
            <div className="p-6 text-neutral-500 text-sm">No match stats yet.</div>
          ) : matchStats.map((s) => (
            <div key={s.id} className="p-4 flex items-center justify-between text-sm">
              <div>
                <div className="font-display font-bold text-lg uppercase">vs {s.match?.opponent}</div>
                <div className="overline">{s.match?.match_date}</div>
              </div>
              <div className="flex gap-4 text-xs">
                <StatChip label="G" value={s.goals} color="#D90429" />
                <StatChip label="A" value={s.assists} color="#FFD166" />
                <StatChip label="MIN" value={s.minutes} color="#06D6A0" />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Notes" && (
        <div className="space-y-4">
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4 flex gap-2">
            <input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a private coach note…"
              className="flex-1 h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
              data-testid="note-input"
            />
            <button
              onClick={addNote}
              data-testid="add-note-btn"
              className="h-12 px-4 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-bold flex items-center gap-2"
            >
              <PaperPlaneRight size={18} weight="bold" /> Add
            </button>
          </div>
          {notes.length === 0 ? (
            <div className="text-neutral-500 text-sm">No notes yet.</div>
          ) : notes.map((n) => (
            <div key={n.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-4 flex justify-between gap-3">
              <div>
                <div className="text-sm">{n.text}</div>
                <div className="overline mt-2">{n.author_name} • {new Date(n.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => deleteNote(n.id)} className="text-neutral-500 hover:text-[#D90429]" data-testid={`delete-note-${n.id}`}>
                <Trash size={18} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "Documents" && <DocumentsTab playerId={id} />}

      {tab === "Progress" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
          <div className="overline mb-3">Overall Rating over Time</div>
          {progressData.length < 2 ? (
            <div className="text-neutral-500 text-sm p-6 text-center">Add more ratings over time to see progress.</div>
          ) : (
            <div style={{ height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={progressData}>
                  <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                  <YAxis domain={[0, 5]} stroke="#737373" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #262626" }} />
                  <Line type="monotone" dataKey="overall" stroke="#D90429" strokeWidth={2} dot={{ fill: "#D90429" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button onClick={deletePlayer} data-testid="delete-player-btn" className="h-12 px-4 rounded-md border border-[#D90429]/40 text-[#D90429] hover:bg-[#D90429]/10 font-bold text-sm flex items-center gap-2">
          <TrashSimple size={16} weight="bold" /> Delete Player
        </button>
      </div>

      <InviteParentModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        playerIds={[id]}
        defaultEmail={player.email || ""}
        defaultName={player.father_name || player.mother_name || player.guardian || ""}
      />

      {emailOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEmailOpen(false)}>
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Email Report</div>
                <div className="font-display font-black text-xl uppercase mt-1">Send PDF to Parent</div>
              </div>
              <button onClick={() => setEmailOpen(false)} className="p-2 rounded-md hover:bg-neutral-900"><X size={20} weight="bold" /></button>
            </div>
            <label className="block">
              <div className="text-xs text-neutral-500 font-semibold mb-1">Parent Email</div>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                data-testid="email-to-input"
                placeholder="parent@example.com"
                className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
              />
            </label>
            <label className="block">
              <div className="text-xs text-neutral-500 font-semibold mb-1">Personal Message (optional)</div>
              <textarea
                rows={3}
                value={emailMsg}
                onChange={(e) => setEmailMsg(e.target.value)}
                placeholder="Great progress this month…"
                className="w-full p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
              />
            </label>
            <button
              onClick={sendReport}
              disabled={emailing || !emailTo}
              data-testid="email-send-btn"
              className="w-full h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <PaperPlaneTilt size={18} weight="bold" /> {emailing ? "Sending…" : "Send Report"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
    <div className="overline">{label}</div>
    <div className="font-display font-black text-3xl mt-1" style={{ color }}>{value}</div>
  </div>
);

const StatChip = ({ label, value, color }) => (
  <div className="text-center">
    <div className="overline">{label}</div>
    <div className="font-display font-bold text-lg" style={{ color }}>{value}</div>
  </div>
);

const InfoGrid = ({ rows }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
    {rows.map(([k, v]) => (
      <div key={k} className="flex justify-between border-b border-neutral-900 pb-2 gap-4">
        <div className="text-sm text-neutral-500">{k}</div>
        <div className="text-sm font-semibold text-right">{v || "—"}</div>
      </div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    present: { bg: "#06D6A0", fg: "#050505", label: "Present" },
    absent: { bg: "#D90429", fg: "#fff", label: "Absent" },
    late: { bg: "#FFD166", fg: "#050505", label: "Late" },
    injured: { bg: "#F97316", fg: "#050505", label: "Injured" },
  };
  const c = map[status] || { bg: "#404040", fg: "#fff", label: status };
  return <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
};
